export const QUERY_PARAM_SERIALIZER_TYPES = {
  CUSTOMER_TAGS_EXCLUDING_PROGRAM_CODE: "customerTagsExcludingProgramCode",
};

export const QUERY_FILTER_TYPES = {
  TEXT: "text",
  SINGLE_SELECT: "singleSelect",
  MULTI_SELECT: "multiSelect",
  MULTI_SELECT_IDS: "multiSelectIds",
  BOOLEAN: "boolean",
};

export const parseCsvParam = (value, delimiter = ",") =>
  String(value || "")
    .split(delimiter)
    .map((item) => item.trim())
    .filter(Boolean);

export const parseBooleanParam = (value) => {
  if (value === "true") return true;
  if (value === "false") return false;
  return null;
};

export const normalizeToStringArray = (
  value,
  objectValueKeys = ["id", "value"]
) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      if (entry == null) return null;
      if (typeof entry !== "object") return String(entry);
      for (const key of objectValueKeys) {
        if (entry[key] != null) return String(entry[key]);
      }
      return null;
    })
    .filter(Boolean);
};

export const getMapKeyByValue = (valueMap, targetValue) => {
  if (!valueMap || targetValue == null) return "";
  const match = Object.entries(valueMap).find(([, value]) => value === targetValue);
  return match ? match[0] : "";
};

const PAGINATION_PARAM_KEYS = {
  page: "page",
  pageSize: "pageSize",
  sort: "sort",
};

const hasFilterValue = (value, valueType) => {
  if (
    valueType === QUERY_FILTER_TYPES.MULTI_SELECT ||
    valueType === QUERY_FILTER_TYPES.MULTI_SELECT_IDS
  ) {
    return Array.isArray(value) && value.length > 0;
  }
  if (valueType === QUERY_FILTER_TYPES.BOOLEAN) {
    return value !== null;
  }
  return Boolean(value);
};

const parseParamValue = (paramValue, valueType) => {
  if (
    valueType === QUERY_FILTER_TYPES.MULTI_SELECT ||
    valueType === QUERY_FILTER_TYPES.MULTI_SELECT_IDS
  ) {
    return parseCsvParam(paramValue);
  }
  if (valueType === QUERY_FILTER_TYPES.BOOLEAN) {
    return parseBooleanParam(paramValue);
  }
  return paramValue || "";
};

const upsertFilterValue = (filters, filterId, value, { markAsDefault = false } = {}) => {
  const filterIdx = filters.findIndex((filter) => filter.id === filterId);
  const nextFilter = {
    id: filterId,
    value,
    ...(markAsDefault ? { isdefault: true } : {}),
  };

  if (filterIdx > -1) {
    filters[filterIdx] = { ...filters[filterIdx], ...nextFilter };
  } else {
    filters.push(nextFilter);
  }
};

const parseSortParam = (sortParam) =>
  String(sortParam || "")
    .split(",")
    .map((part) => {
      const [id, direction] = part.split(":");
      if (!id) {
        return null;
      }
      return { id, desc: direction === "desc" };
    })
    .filter(Boolean);

const createSortUtils = (defaultSort = [], stripSortIds = []) => {
  const stripSort = (sorted = []) =>
    sorted.filter((sort) => !stripSortIds.includes(sort.id));

  const serializeSortParam = (sorted = []) =>
    stripSort(sorted)
      .map(({ id, desc }) => `${id}:${desc ? "desc" : "asc"}`)
      .join(",");

  const isDefaultSort = (sorted = []) => {
    const normalizedSorted = stripSort(sorted);
    const normalizedDefaultSort = stripSort(defaultSort);
    if (normalizedSorted.length !== normalizedDefaultSort.length) {
      return false;
    }
    return normalizedSorted.every(
      (sort, index) =>
        sort.id === normalizedDefaultSort[index]?.id &&
        sort.desc === normalizedDefaultSort[index]?.desc
    );
  };

  return { stripSort, serializeSortParam, isDefaultSort };
};

export const getFilterValueFromUrl = (config, urlFilterValues) => {
  if (typeof config.parseFromUrl === "function") {
    return config.parseFromUrl(urlFilterValues[config.paramKey], urlFilterValues);
  }

  const rawValue = urlFilterValues[config.paramKey];
  if (config.valueMap && typeof rawValue === "string" && rawValue) {
    return (
      config.valueMap[rawValue.toUpperCase()] || config.valueMap[rawValue] || ""
    );
  }
  return rawValue;
};

export const serializeFilterForUrl = (filter, config, urlFilterValues = {}) => {
  if (typeof config.omitFromUrlWhen === "function" && config.omitFromUrlWhen(filter)) {
    return "";
  }

  if (typeof config.serializeToUrl === "function") {
    return config.serializeToUrl(filter, urlFilterValues);
  }

  if (!filter?.value && filter?.value !== 0 && filter?.value !== false) {
    return "";
  }

  if (config.serializer === QUERY_PARAM_SERIALIZER_TYPES.CUSTOMER_TAGS_EXCLUDING_PROGRAM_CODE) {
    const selectedTags = normalizeToStringArray(filter?.value);
    return selectedTags
      .filter((tag) => tag !== (urlFilterValues.faxProgramCode || ""))
      .join(",");
  }

  if (config.valueType === QUERY_FILTER_TYPES.BOOLEAN) {
    return typeof filter.value === "boolean" ? String(filter.value) : "";
  }

  if (
    config.valueType === QUERY_FILTER_TYPES.MULTI_SELECT ||
    config.valueType === QUERY_FILTER_TYPES.MULTI_SELECT_IDS
  ) {
    return normalizeToStringArray(filter.value, ["id", "name", "value"]).join(",");
  }

  if (config.valueMap) {
    return getMapKeyByValue(config.valueMap, filter.value);
  }

  return filter?.value ? String(filter.value) : "";
};

/**
 * Factory for list URL filter sync. Each list supplies its own filter config constant.
 */
export const createListUrlFilterUtils = ({
  filterConfig,
  staticParamKeys = [],
  includePaginationParams = false,
  defaultPageSize,
  defaultSort = [],
  stripSortIds = [],
  markBuiltFiltersAsDefault = false,
  shouldSkipFilterWhenBuilding,
  appendBuiltFilters,
}) => {
  const { stripSort, serializeSortParam, isDefaultSort } = createSortUtils(
    defaultSort,
    stripSortIds
  );

  const getUrlFilterValues = (search) => {
    const params = new URLSearchParams(search || "");
    const values = {};

    staticParamKeys.forEach((key) => {
      values[key] = params.get(key);
    });

    if (includePaginationParams) {
      values[PAGINATION_PARAM_KEYS.page] = params.get(PAGINATION_PARAM_KEYS.page);
      values[PAGINATION_PARAM_KEYS.pageSize] = params.get(PAGINATION_PARAM_KEYS.pageSize);
      values[PAGINATION_PARAM_KEYS.sort] = params.get(PAGINATION_PARAM_KEYS.sort);
    }

    filterConfig.forEach(({ paramKey, valueType }) => {
      values[paramKey] = parseParamValue(params.get(paramKey), valueType);
    });

    return values;
  };

  const buildPrimaryFilters = (currentFilters, urlFilterValues, context = {}) => {
    const newFilters = JSON.parse(JSON.stringify(currentFilters));

    filterConfig.forEach((config) => {
      if (shouldSkipFilterWhenBuilding?.(config, context)) {
        return;
      }

      const filterValue = getFilterValueFromUrl(config, urlFilterValues);
      if (!hasFilterValue(filterValue, config.valueType)) {
        return;
      }

      upsertFilterValue(newFilters, config.filterId, filterValue, {
        markAsDefault: markBuiltFiltersAsDefault,
      });
    });

    return appendBuiltFilters
      ? appendBuiltFilters(newFilters, context)
      : newFilters;
  };

  const buildSearchFromPagination = (pagination, currentSearch = "") => {
    const params = new URLSearchParams(currentSearch || "");
    const urlFilterValues = getUrlFilterValues(currentSearch);

    filterConfig.forEach((config) => {
      const filter = pagination.filtered.find((item) => item.id === config.filterId);
      const serializedValue = serializeFilterForUrl(filter, config, urlFilterValues);

      if (serializedValue) {
        params.set(config.paramKey, serializedValue);
      } else {
        params.delete(config.paramKey);
      }
    });

    if (includePaginationParams) {
      if (pagination.page > 0) {
        params.set(PAGINATION_PARAM_KEYS.page, String(pagination.page));
      } else {
        params.delete(PAGINATION_PARAM_KEYS.page);
      }

      if (
        defaultPageSize != null &&
        pagination.pageSize !== defaultPageSize
      ) {
        params.set(PAGINATION_PARAM_KEYS.pageSize, String(pagination.pageSize));
      } else {
        params.delete(PAGINATION_PARAM_KEYS.pageSize);
      }

      const sortParam = serializeSortParam(pagination.sorted);
      if (sortParam && !isDefaultSort(pagination.sorted)) {
        params.set(PAGINATION_PARAM_KEYS.sort, sortParam);
      } else {
        params.delete(PAGINATION_PARAM_KEYS.sort);
      }
    }

    return params.toString() ? `?${params.toString()}` : "";
  };

  const buildInitialPaginationFromUrl = (
    search,
    basePagination,
    { shouldReset = false } = {}
  ) => {
    if (shouldReset) {
      return basePagination;
    }

    const urlFilterValues = getUrlFilterValues(search);
    const params = new URLSearchParams(search || "");
    const filtered = buildPrimaryFilters(basePagination.filtered, urlFilterValues);

    if (!includePaginationParams) {
      return {
        ...basePagination,
        filtered,
      };
    }

    const page = params.has(PAGINATION_PARAM_KEYS.page)
      ? Number(params.get(PAGINATION_PARAM_KEYS.page))
      : basePagination.page;
    const pageSize = params.has(PAGINATION_PARAM_KEYS.pageSize)
      ? Number(params.get(PAGINATION_PARAM_KEYS.pageSize))
      : basePagination.pageSize;
    const sorted = params.get(PAGINATION_PARAM_KEYS.sort)
      ? parseSortParam(params.get(PAGINATION_PARAM_KEYS.sort))
      : basePagination.sorted;

    return {
      ...basePagination,
      filtered,
      page: Number.isFinite(page) ? page : basePagination.page,
      pageSize: Number.isFinite(pageSize) ? pageSize : basePagination.pageSize,
      sorted: sorted.length ? sorted : basePagination.sorted,
    };
  };

  return {
    getUrlFilterValues,
    getFilterValueFromUrl,
    buildPrimaryFilters,
    buildSearchFromPagination,
    buildInitialPaginationFromUrl,
    stripSort,
  };
};

